import { cn } from '@worldcoo/ui/lib/utils';
import { ArrowUpRight, MenuIcon, XIcon } from 'lucide-react';
import { Sheet, SheetClose, SheetContent, SheetTitle, SheetTrigger } from './snap-sheet';
import { Link } from '@tanstack/react-router';
import { useLingui } from '@lingui/react/macro';
import { useCallback, useRef } from 'react';
import type { PortalLocale } from '@worldcoo/monolith/portals';
import { WorldcooLogo } from '@worldcoo/ui/custom/worldcoo-logo';
import { DONOR_APP_URL } from './external-links';
import { LanguageFlag, LANGUAGES } from './languages';
import { MOBILE_MENU_BUTTON, useNavLinks } from './nav';
import { DiamondGrid, diamondGlows } from './primitives';

/** 20px in the hand-off. */
function ArrowBadge() {
	return (
		<span aria-hidden className="bg-ink grid size-5 shrink-0 place-items-center rounded-full text-white">
			<ArrowUpRight className="size-3" />
		</span>
	);
}

/** The header row repeats the page header's metrics (95px, `px-6`, 48px square) so close lands where the burger was. */
export function MobileMenu(props: {
	language: PortalLocale;
	changeLanguage: (lang: PortalLocale) => void;
	open: boolean;
	onOpenChange: (open: boolean) => void;
}) {
	const { t } = useLingui();
	const navLinks = useNavLinks();
	const closeRef = useRef<HTMLButtonElement>(null);
	const { onOpenChange } = props;
	// Stable, because the sheet's gesture effect re-subscribes when its dismiss callback changes.
	const dismiss = useCallback(() => onOpenChange(false), [onOpenChange]);

	return (
		<Sheet open={props.open} onOpenChange={props.onOpenChange}>
			<SheetTrigger className={MOBILE_MENU_BUTTON} aria-label={t`Open navigation menu`}>
				<MenuIcon className="size-6" />
			</SheetTrigger>
			<SheetContent
				panelClassName="bg-white"
				onDismiss={dismiss}
				aria-describedby={undefined}
				onOpenAutoFocus={(e) => {
					e.preventDefault();
					closeRef.current?.focus();
				}}
			>
				<DiamondGrid
					color="var(--color-brand)"
					opacity={0.9}
					masks={[diamondGlows({ x: '50%', y: 'calc(100% - 118px)' })]}
				/>

				<div className="relative flex h-[95px] shrink-0 items-center px-6">
					<SheetClose asChild>
						<Link to="/www" activeOptions={{ exact: true }} className="shrink-0">
							<span className="sr-only">{t`Worldcoo home`}</span>
							<WorldcooLogo className="h-11 w-auto" accentClassName="text-brand" />
						</Link>
					</SheetClose>
					<SheetClose ref={closeRef} className={MOBILE_MENU_BUTTON} aria-label={t`Close`}>
						<XIcon className="size-6" />
					</SheetClose>
					<div className="sr-only">
						<SheetTitle>{t`Navigation menu`}</SheetTitle>
					</div>
				</div>

				{/* No `Home` row, as the hand-off draws it: the logo is the way back. */}
				<nav className="text-ink relative flex flex-col gap-[30px] px-6 pt-16 text-2xl">
					{navLinks.map((item) => (
						<SheetClose asChild key={item.to}>
							<Link to={item.to} className="flex items-center justify-between gap-4">
								<span className="aria-[current=page]:font-medium">{item.label}</span>
								<ArrowBadge />
							</Link>
						</SheetClose>
					))}
				</nav>

				<div className="relative flex flex-col gap-3 px-6 pt-16">
					<SheetClose asChild>
						<Link
							to="/www/contact"
							className="bg-brand hover:bg-brand-strong flex h-[54px] items-center justify-center gap-2 rounded-full font-medium text-black transition-colors"
						>
							{t`Contact us`}
							<ArrowBadge />
						</Link>
					</SheetClose>
					<a
						href={DONOR_APP_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="text-ink flex h-[54px] items-center justify-center gap-2 font-medium"
					>
						{t`Donation certificate`}
						<ArrowBadge />
					</a>
				</div>

				<div className="relative mt-auto flex items-center gap-1 px-6 pb-6">
					{LANGUAGES.map((lang) => (
						<button
							key={lang.code}
							type="button"
							onClick={() => props.changeLanguage(lang.code)}
							aria-current={props.language === lang.code ? 'true' : undefined}
							// `px-2`: at `px-2.5` the fifth flag crossed the 327px content box's gutter.
							className={cn(
								'text-ink flex cursor-pointer items-center gap-1.5 rounded-xl px-2 py-1.5 text-base transition-colors',
								props.language === lang.code ? 'border-ink/25 border' : 'hover:bg-ink/5',
							)}
						>
							<LanguageFlag code={lang.code} className="size-5 shrink-0" />
							{lang.short}
						</button>
					))}
				</div>
			</SheetContent>
		</Sheet>
	);
}
